import { IconShieldCheck, IconZoomOutline } from '@iwoca/icons';
import { Input, Link } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';

import styles from './BankSelectionModal.module.css';
import { useLocale } from '../../../../hooks/useLocale';
import { TOnlineBank } from '../OpenBanking.types';
import TrueLayerShareableLink from '../TrueLayerShareableLink/TrueLayerShareableLink';

const BankListView = ({
  banks,
  onSearch,
  searchTerm,
  onBankSelect,
  ableToShareLink,
}: {
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBankSelect: (bank: TOnlineBank) => void;
  banks: TOnlineBank[];
  searchTerm: string;
  ableToShareLink: boolean;
}) => {
  const { t } = useLingui();
  const { isUK, isDE } = useLocale();

  return (
    <>
      {isUK && ableToShareLink && (
        <>
          <TrueLayerShareableLink noBankLogin />
          <hr className={styles.divider} />
        </>
      )}
      <div className={styles.bankListHeader}>
        <h3 className={styles.bankListTitle}>
          <Trans>How does it work?</Trans>
        </h3>
        <div className={styles.bankGradeContainer}>
          <IconShieldCheck className={styles.bankGradeIcon} />
          <span>
            <Trans>Bank-grade encryption</Trans>
          </span>
        </div>
      </div>
      <p className={styles.bankListDescription}>
        <Trans>
          Securely share your bank account’s data. Simply choose your bank below
          and then log in through your bank’s portal when prompted.
        </Trans>
      </p>
      <Input
        iconLeft={<IconZoomOutline />}
        className={styles.searchBar}
        placeholder={t`Search for your bank...`}
        onChange={onSearch}
        value={searchTerm}
      />
      <div
        className={clsx(styles.banksGridContainer, {
          [styles.bankButtonError]: banks.length < 1,
        })}
      >
        {banks?.map((bank) => {
          return (
            <button
              key={bank.bank_id}
              className={styles.bankButton}
              onClick={() => onBankSelect(bank)}
            >
              <figure className={styles.bankButtonFigure}>
                <img
                  src={bank.logo_url}
                  alt={bank.bank_name}
                  className={clsx('fs-mask dd-privacy-mask', styles.bankLogo)}
                />
                <figcaption className={styles.bankName}>
                  {bank.bank_name}
                </figcaption>
              </figure>
            </button>
          );
        })}
        {banks.length < 1 && (
          <p className={styles.bankListError}>
            <Trans>
              Unfortunately the bank you have chosen is not supported.
            </Trans>
          </p>
        )}
      </div>
      {!isDE && (
        <div
          className={styles.trueLayerFooter}
          data-testid="true-layer-modal-footer"
        >
          <p>
            <small>
              We’re{' '}
              <Link
                data-testid="fca-regulated-link"
                href="https://www.fca.org.uk/about/what-we-do/the-fca"
                target="_blank"
                rel="noopener noreferrer"
              >
                FCA registered
              </Link>{' '}
              which allows us to offer Open Banking
            </small>
          </p>
        </div>
      )}
    </>
  );
};

export default BankListView;
